import { fabric } from 'fabric';

import { ARTBOARD_PRESETS, artboardFromImage } from './artboard';
import { resolveBrand } from './colors';
import { isTextObject } from './fabric-setup';
import { DEFAULT_PAIR, FONT_PAIRS } from './fonts';
import { setImageLoader } from './objects';
import { findTemplate, isBlankText, TEMPLATE_IDS, TemplateCopy } from './templates';
import type { ArtboardSize, BrandInfo, Translate } from './types';

/**
 * A design an agent asked for "in the style of" one of the editor's templates.
 *
 * The planner is synchronous and knows nothing of fonts or bitmaps, so it
 * records the request (this payload, under `csTemplate` on the scene) and the
 * template is laid out later, wherever the scene is first drawn: the headless
 * renderer (canvas/design-render.ts) or the editor, if the vendor opens it
 * before that. Both turn it into an ordinary editor scene (csScene 2).
 */
export interface TemplateRequest {
    id: string;
    eyebrow?: string;
    headline: string;
    subheadline?: string;
    cta?: string;
    spec?: string;
    /** "1:1" | "4:5" | "9:16" | "16:9" | "2:3"; absent = the picture's own shape. */
    aspectRatio?: string;
    /** The brand, as far as the planner knew it. */
    brand?: {
        fontFamily?: string;
        textColor?: string;
        colors?: string[];
        colorRoles?: { ink?: string; ground?: string; accent?: string; onAccent?: string };
        brandName?: string;
        logoUrl?: string;
    };
}

export const isTemplateId = (id?: string | null): boolean => !!id && TEMPLATE_IDS.includes(id);

/** The artboard for a requested shape, or the picture's own shape. */
export function artboardForAspect(aspectRatio: string | undefined, imageW: number, imageH: number): ArtboardSize {
    const preset = ARTBOARD_PRESETS.find((p) => p.ratio === String(aspectRatio || '').trim());
    if (preset) return { w: preset.w, h: preset.h };
    return artboardFromImage(imageW, imageH);
}

export function brandFromRequest(req: TemplateRequest): BrandInfo {
    const b = req.brand || {};
    return resolveBrand(
        {
            vendor: '',
            brandName: b.brandName,
            logoUrl: b.logoUrl,
            primaryColors: [...(b.colors || []), ...(b.textColor ? [b.textColor] : [])],
            secondaryColors: [],
            primaryFont: b.fontFamily,
            styleKeywords: [],
            referenceImageUrls: [],
            bannedWords: [],
            colorRoles: b.colorRoles,
        } as any,
        undefined,
    );
}

/**
 * Lay a template out for real and return the editor scene (csScene 2): the
 * picture as the locked background / image slots, the agent's words in the
 * template's text slots, the brand's colours, typeface and logo.
 *
 * Built on an offscreen StaticCanvas and serialised with toJSON, so every
 * custom property (roles, plates, masks) survives exactly as the editor saves
 * them. `loadImage` can stand in for the network (tests).
 */
export async function buildTemplateScene(opts: {
    request: TemplateRequest;
    imageUrl: string;
    imageWidth: number;
    imageHeight: number;
    t: Translate;
    loadImage?: (url: string) => Promise<HTMLImageElement>;
}): Promise<any> {
    const { request, imageUrl, t } = opts;
    const def = findTemplate(request.id);
    if (!def) throw new Error(`Unknown design template "${request.id}"`);
    const art = artboardForAspect(request.aspectRatio, opts.imageWidth, opts.imageHeight);
    const brand = brandFromRequest(request);
    const copy: TemplateCopy = {
        eyebrow: request.eyebrow,
        headline: request.headline,
        sub: request.subheadline,
        cta: request.cta,
        spec: request.spec,
    };
    // A brand face carries the headings; otherwise the editorial pairing.
    const pair = FONT_PAIRS.find((p) => p.id === 'editorial') || DEFAULT_PAIR;

    const restore = opts.loadImage ? setImageLoader(opts.loadImage) : null;
    let res;
    try {
        res = await def.build({ art, brand, pair, t, images: [imageUrl], useBrandFont: true, copy });
    } finally {
        restore?.();
    }

    const canvas = new fabric.StaticCanvas(null as any, {
        width: art.w,
        height: art.h,
        enableRetinaScaling: false,
        renderOnAddRemove: false,
    } as any);
    try {
        canvas.backgroundColor = res.background;
        res.objects
            .filter((o) => !(isTextObject(o) && isBlankText(o)))
            .forEach((o) => canvas.add(o));
        const json: any = canvas.toJSON();
        delete json.clipPath;
        delete json.backgroundImage;
        json.csScene = 2;
        json.artboardWidth = art.w;
        json.artboardHeight = art.h;
        json.authoredWidth = art.w;
        json.csTemplateId = request.id;
        return json;
    } finally {
        canvas.dispose();
    }
}
